import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { IconEyeFilled, IconAwardFilled } from '@tabler/icons-react';
import LockIcon from '@mui/icons-material/Lock';
import { CardContainerRetro } from '../Container/CardContainerRetro';
import { CardContainer } from '../Container/CardContainer';
import { WriterInfo } from '../WriterInfo/WriterInfo';
import MetricStatus from '../MetricStatus/MetricStatus';
import { ThreadsTitle, ThreadsPreview } from '../customTypo/Display';
import { getFirstSentence, getPreviewTextFromThread } from '../../utils/textUtils';
import { useIsMobile } from '../../hooks/useIsMobile';

const POST_VARIANTS = ['post', 'postBig', 'postMetric'];
const COLLECTION_VARIANTS = ['collection', 'myCollection'];

/**
 * CardUnified 컴포넌트
 *
 * Card 패밀리(CardPost / CardPostBig / CardPostMetric / CardCollection /
 * CardMyCollection)를 단일 컴포넌트로 통합. `variant` prop 으로 레이아웃을
 * 선택하며 각 변형의 기존 시각 출력을 그대로 재현한다.
 *
 * @param {('post'|'postBig'|'postMetric'|'collection'|'myCollection')} variant - 렌더링할 카드 형태 [Optional, 기본값: 'post']
 * @param {Object} thread - 스레드 데이터 (post 계열에서 사용) [variant=post* 시 Required]
 * @param {Object} collection - 컬렉션 데이터 (collection 계열에서 사용) [variant=collection* 시 Required]
 * @param {string} date - 표시할 날짜. 미지정 시 thread.timestamp 사용 [Optional]
 * @param {number} rd - 카드 모서리 둥글기(px) [Optional, 기본값: 8]
 * @param {('full'|'bottom'|'left'|'right'|'top')} borderType - 테두리 형태 [Optional, 기본값: 'full']
 * @param {boolean} isRemarkable - 어워드 아이콘 강제 노출 여부 (post / postMetric) [Optional, 기본값: false]
 * @param {boolean} isRanked - 랭크 배지 노출 여부 (post / postMetric) [Optional, 기본값: false]
 * @param {number} rank - 랭크 배지 숫자 [Optional, 기본값: 0]
 * @param {boolean} isMobile - 모바일 레이아웃 강제 적용. 미지정 시 내부 useIsMobile 사용 [Optional]
 * @param {boolean} isMetric - 메트릭(조회/좋아요/댓글/리포스트) 노출 여부 (post) [Optional, 기본값: true]
 * @param {boolean} isMe - 내 컬렉션 여부. 링크 경로/UserPage 링크 제어 (collection 계열) [Optional, 기본값: false]
 *
 * Example usage:
 * <CardUnified variant="post" thread={thread} date={thread.timestamp} isMobile={isMobile} />
 * <CardUnified variant="collection" collection={collection} isMe />
 */
export const CardUnified = ({
  variant = 'post',
  thread,
  collection,
  date,
  rd = 8,
  borderType = 'full',
  isRemarkable = false,
  isRanked = false,
  rank = 0,
  isMobile,
  isMetric = true,
  isMe = false,
}) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  // collection 변형은 'sm' 기준 자체 판단(기존 CardCollection 동작), 그 외는 prop 우선
  const autoIsMobile = useIsMobile('sm');
  const resolvedIsMobile =
    typeof isMobile === 'boolean' ? isMobile : autoIsMobile;

  if (COLLECTION_VARIANTS.includes(variant)) {
    // collection: 원본은 항상 useIsMobile('sm') 사용(prop 무시).
    // myCollection: 원본은 isMobile prop 사용.
    const collectionIsMobile =
      variant === 'collection' ? autoIsMobile : resolvedIsMobile;
    return renderCollection({
      variant,
      collection,
      rd,
      borderType,
      isMe,
      isMobile: collectionIsMobile,
      theme,
    });
  }

  if (POST_VARIANTS.includes(variant)) {
    return renderPost({
      variant,
      thread,
      date,
      rd,
      borderType,
      isRemarkable,
      isRanked,
      rank,
      isMetric,
      isMobile: typeof isMobile === 'boolean' ? isMobile : false,
      theme,
      i18n,
    });
  }

  return null;
};

/**
 * post 계열(post / postBig / postMetric) 렌더링.
 * 원본 CardPost / CardPostBig / CardPostMetric 의 레이아웃을 분기 재현.
 */
function renderPost({
  variant,
  thread,
  date,
  rd,
  borderType,
  isRemarkable,
  isRanked,
  rank,
  isMetric,
  isMobile,
  theme,
  i18n,
}) {
  const isBig = variant === 'postBig';
  const isMetricVariant = variant === 'postMetric';

  // 컨테이너: postMetric 만 CardContainer(라이트 보더), 나머지 CardContainerRetro
  const Container = isMetricVariant ? CardContainer : CardContainerRetro;
  const containerProps = isMetricVariant
    ? { w: '100%', rd, borderType }
    : isBig
      ? { w: '100%', h: 336.62, rd, borderType }
      : { w: '100%', rd, borderType };

  // 날짜 처리: postBig 는 i18n locale 적용 + fallback, 그 외는 기존 그대로
  const timestamp = isBig ? date || thread?.timestamp : date;
  let formattedDate;
  let formattedDay;
  if (isBig) {
    const momentInstance = timestamp
      ? moment(timestamp).locale(i18n.language)
      : null;
    formattedDate = momentInstance ? momentInstance.format('MM/DD') : '--';
    formattedDay = momentInstance ? momentInstance.format('ddd') : '--';
  } else {
    formattedDate = moment(timestamp).format('MM/DD');
    formattedDay = moment(timestamp).format('ddd');
  }

  // 랭크 비교값 (postBig 는 안전 변환, 나머지는 직접 비교)
  const viewRank = isBig
    ? typeof thread?.viewRank === 'number'
      ? thread.viewRank
      : Number.POSITIVE_INFINITY
    : thread?.viewRank;
  const engagementRank = isBig
    ? typeof thread?.engagementRank === 'number'
      ? thread.engagementRank
      : Number.POSITIVE_INFINITY
    : thread?.engagementRank;

  const showViewIcon = viewRank < 4;
  const showAwardIcon = isBig
    ? engagementRank < 4
    : engagementRank < 4 || isRemarkable === true;

  // 외곽 컨텐츠 박스 gap: post=4, postBig=2, postMetric=2
  const contentGap = variant === 'post' ? theme.spacing(4) : theme.spacing(2);

  // postMetric 은 hover 반전/cursor 없음
  const interactiveSx = isMetricVariant
    ? {}
    : {
        transition:
          'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          transform: 'none',
          '& *': {
            color: theme.palette.background.paper + ' !important',
          },
          '& svg': {
            color: theme.palette.background.paper + ' !important',
          },
        },
      };

  const contentPadding = isMetricVariant
    ? theme.spacing(1.5, 2)
    : isMobile
      ? theme.spacing(1, 1.5)
      : theme.spacing(1.5, 2);

  const permalink = isBig ? thread?.permalink || '#' : thread?.permalink;

  return (
    <Container {...containerProps}>
      <Box
        sx={{
          padding: contentPadding,
          width: '100%',
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          gap: contentGap,
          borderRadius: theme.spacing(0.5),
          ...interactiveSx,
        }}
      >
        {isMetricVariant ? (
          <PostMetricBody
            thread={thread}
            rank={rank}
            isRanked={isRanked}
            showViewIcon={showViewIcon}
            showAwardIcon={showAwardIcon}
            formattedDate={formattedDate}
            formattedDay={formattedDay}
            permalink={permalink}
            theme={theme}
          />
        ) : (
          <PostBody
            isBig={isBig}
            thread={thread}
            rank={rank}
            isRanked={isRanked}
            isMobile={isMobile}
            showViewIcon={showViewIcon}
            showAwardIcon={showAwardIcon}
            formattedDate={formattedDate}
            formattedDay={formattedDay}
            permalink={permalink}
            theme={theme}
          />
        )}
        {isMetricVariant && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ paddingLeft: 0, color: theme.palette.text.secondary }}
          >
            <MetricStatus type={'view'} value={thread.views} />
            <MetricStatus type={'like'} value={thread.likes} />
            <MetricStatus type={'reply'} value={thread.replies} />
            <MetricStatus type={'repost'} value={thread.reposts} />
          </Stack>
        )}
        {isBig && (
          <Stack
            direction="row"
            spacing={isMobile ? 0 : 1}
            sx={{
              color: theme.palette.text.secondary,
              flexWrap: 'wrap',
              mt: -0.5,
            }}
          >
            <PostMetricRow
              thread={thread}
              isMobile={isMobile}
              hasNumericFallback
            />
          </Stack>
        )}
        {variant === 'post' && isMetric && (
          <Box
            sx={{
              display: 'flex',
              gap: isMobile ? 0 : theme.spacing(1),
              color: theme.palette.text.secondary,
              flexWrap: 'wrap',
            }}
          >
            <PostMetricRow thread={thread} isMobile={isMobile} />
          </Box>
        )}
      </Box>
    </Container>
  );
}

/**
 * post / postBig 공통 본문(헤더 + 본문 텍스트).
 */
function PostBody({
  isBig,
  thread,
  rank,
  isRanked,
  isMobile,
  showViewIcon,
  showAwardIcon,
  formattedDate,
  formattedDay,
  permalink,
  theme,
}) {
  const displayHeadings = theme.other?.displayHeadings || {};
  const displayPrimary = displayHeadings.d4 || {};
  const displaySecondary = displayHeadings.d5 || displayHeadings.d4 || {};
  const titleVariant = isMobile ? 'h6' : 'h5';
  const titleText = getFirstSentence(thread?.text);
  const previewText = getPreviewTextFromThread(
    thread,
    isBig ? (isMobile ? 120 : 200) : isMobile ? 100 : 140
  );

  return (
    <Stack spacing={0}>
      {/* post 변형: 랭크 배지가 헤더 위에 단독 표시 */}
      {!isBig && isRanked && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              lineHeight: 1,
              color: theme.palette.background.paper,
              fontSize: '0.875rem',
            }}
          >
            {rank}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          height: isBig ? 20 : 18,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: theme.spacing(0.5),
            alignItems: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              lineHeight: 1,
              color: theme.palette.text.secondary,
            }}
          >
            {formattedDate}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              lineHeight: 1,
              color: theme.palette.text.secondary,
            }}
          >
            {formattedDay}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: theme.spacing(0.25),
            flexDirection: 'row',
            alignItems: 'center',
            ...(isBig ? { color: theme.palette.text.secondary } : {}),
          }}
        >
          {showViewIcon && (
            <Box
              sx={{
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconEyeFilled size={18} />
            </Box>
          )}
          {showAwardIcon && (
            <Box
              sx={{
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconAwardFilled size={18} />
            </Box>
          )}
        </Box>
      </Box>
      <a
        href={permalink}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: 'none' }}
      >
        {isBig && (
          <ThreadsTitle
            variant={titleVariant}
            fw={
              (isMobile
                ? displaySecondary.fontWeight
                : displayPrimary.fontWeight) || 900
            }
            sx={{
              color: theme.palette.text.primary,
              marginBottom: isMobile ? theme.spacing(3) : theme.spacing(6),
              width: isMobile ? '100%' : 'calc(100% - 16px)',
              overflow: 'hidden',
            }}
          >
            {titleText}
          </ThreadsTitle>
        )}
        <ThreadsPreview
          variant={isBig ? 'body2' : 'body1'}
          lines={isBig ? 3 : 2}
          sx={{
            width: isMobile ? '100%' : 'calc(100% - 16px)',
            color: theme.palette.text.primary,
            ...(isBig
              ? {}
              : { wordBreak: 'keep-all', overflowWrap: 'break-word' }),
          }}
        >
          {previewText}
        </ThreadsPreview>
      </a>
    </Stack>
  );
}

/**
 * postMetric 본문(헤더 + 날짜 + 본문 텍스트). 원본 CardPostMetric 레이아웃 재현.
 */
function PostMetricBody({
  thread,
  rank,
  isRanked,
  showViewIcon,
  showAwardIcon,
  formattedDate,
  formattedDay,
  permalink,
  theme,
}) {
  return (
    <Stack spacing={0.5}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0}
        sx={{ height: 20 }}
      >
        {isRanked && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: theme.palette.text.primary,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                lineHeight: 1,
                color: theme.palette.background.paper,
                fontSize: '0.875rem',
              }}
            >
              {rank}
            </Typography>
          </Box>
        )}
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ color: theme.palette.text.secondary }}
        >
          {showViewIcon && (
            <IconEyeFilled color={theme.palette.primary.main} size={18} />
          )}
          {showAwardIcon && (
            <IconAwardFilled
              color={theme.palette.text.primary}
              size={18}
              style={{ marginRight: '-8px' }}
            />
          )}
        </Stack>
      </Stack>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ mt: 0.5 }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            lineHeight: 1,
            color: theme.palette.text.secondary,
          }}
        >
          {formattedDate}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            lineHeight: 1,
            color: theme.palette.text.secondary,
          }}
        >
          {formattedDay}
        </Typography>
      </Stack>
      <a href={permalink} target="_blank" rel="noreferrer">
        <ThreadsPreview
          variant="body1"
          lines={2}
          sx={{
            width: 'calc(100% - 16px)',
            color: theme.palette.text.primary,
          }}
        >
          {getPreviewTextFromThread(thread, 140)}
        </ThreadsPreview>
      </a>
    </Stack>
  );
}

/**
 * post / postBig 공통 4-메트릭 행(조회/좋아요/댓글/리포스트).
 *
 * @param {Object} thread - 스레드 데이터
 * @param {boolean} isMobile - 모바일 레이아웃 여부
 * @param {boolean} hasNumericFallback - 숫자가 아닐 때 0으로 보정 (postBig 동작) [기본값: false]
 */
function PostMetricRow({ thread, isMobile, hasNumericFallback = false }) {
  const toValue = (v) =>
    hasNumericFallback ? (typeof v === 'number' ? v : 0) : v;
  const views = toValue(thread?.views);
  const likes = toValue(thread?.likes);
  const replies = toValue(thread?.replies);
  const reposts = toValue(thread?.reposts);

  return (
    <>
      <MetricStatus
        w={isMobile ? '50%' : 'auto'}
        isMobile={isMobile}
        type={'view'}
        value={views}
      />
      <MetricStatus
        w={isMobile ? '50%' : 'auto'}
        isMobile={isMobile}
        type={'like'}
        value={likes}
      />
      <MetricStatus
        w={isMobile ? '50%' : 'auto'}
        isMobile={isMobile}
        type={'reply'}
        value={replies}
      />
      <MetricStatus
        w={isMobile ? '50%' : 'auto'}
        isMobile={isMobile}
        type={'repost'}
        value={reposts}
      />
    </>
  );
}

/**
 * collection / myCollection 렌더링. 원본 CardCollection / CardMyCollection 재현.
 */
function renderCollection({
  variant,
  collection,
  rd,
  borderType,
  isMe,
  isMobile,
  theme,
}) {
  const isMyCollection = variant === 'myCollection';
  const displayHeadings = theme.other?.displayHeadings || {};
  const displayLarge = isMobile
    ? displayHeadings.d5 || {}
    : displayHeadings.d4 || {};
  // CardCollection 은 h4/h6, CardMyCollection 도 h4/h6 (동일)
  const titleVariant = isMobile ? 'h6' : 'h4';

  return (
    <NavLink
      to={
        isMe
          ? `/my-collection/${collection.id}`
          : `/collection/${collection.id}`
      }
      style={{ width: '100%' }}
    >
      <CardContainerRetro w={'100%'} rd={rd} borderType={borderType}>
        {/* 비공개 컬렉션 자물쇠 아이콘 */}
        {collection.isPublic === false && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
            }}
          >
            <LockIcon
              sx={{
                fontSize: 20,
                color: 'black',
              }}
            />
          </Box>
        )}
        {isMyCollection ? (
          <Stack
            spacing={2}
            justifyContent="space-between"
            sx={{
              padding: isMobile ? theme.spacing(2) : theme.spacing(3),
              width: '100%',
              height: isMobile ? '180px' : 'auto',
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.spacing(0.5),
            }}
          >
            <Stack spacing={0.25}>
              <ThreadsTitle
                variant={titleVariant}
                fw={displayLarge.fontWeight || 900}
                sx={{
                  width: isMobile ? '100%' : 'calc(100% - 16px)',
                  color: theme.palette.text.primary,
                  wordBreak: 'keep-all',
                  overflowWrap: 'break-word',
                  overflow: 'hidden',
                  fontSize: displayLarge.fontSize,
                }}
              >
                {collection.title}
              </ThreadsTitle>
              {!isMobile && (
                <Typography
                  sx={{
                    lineHeight: 1.55,
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    color: theme.palette.text.primary,
                    height: isMobile
                      ? `${0.875 * 1.55 * 3}rem`
                      : `${1 * 1.55 * 3}rem`,
                    fontWeight: 600,
                    width: isMobile ? '100%' : 'calc(100% - 16px)',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'keep-all',
                    overflowWrap: 'break-word',
                  }}
                >
                  {collection.description}
                </Typography>
              )}
            </Stack>
            <WriterInfo
              size={isMobile ? 'small' : 'default'}
              profileImage={collection.profileImage}
              userID={collection.userID}
              username={collection.username}
              name={collection.name}
              showUserPageLink={!isMe}
            />
            {!isMobile && (
              <Stack
                direction="row"
                spacing={isMobile ? 1 : 2}
                sx={{
                  color: theme.palette.text.secondary,
                  flexWrap: 'wrap',
                }}
              >
                <MetricStatus
                  w={'auto'}
                  isMobile={isMobile}
                  type={'view'}
                  value={collection.metric?.views}
                  size={isMobile ? 16 : 24}
                />
                <MetricStatus
                  w={'auto'}
                  isMobile={isMobile}
                  type={'like'}
                  value={collection.metric?.likes}
                  size={isMobile ? 16 : 24}
                />
                <MetricStatus
                  w={'auto'}
                  isMobile={isMobile}
                  type={'reply'}
                  value={collection.metric?.replies}
                  size={isMobile ? 16 : 24}
                />
                <MetricStatus
                  w={'auto'}
                  isMobile={isMobile}
                  type={'repost'}
                  value={collection.metric?.reposts}
                  size={isMobile ? 16 : 24}
                />
              </Stack>
            )}
          </Stack>
        ) : (
          <Box
            sx={{
              padding: isMobile ? theme.spacing(2) : theme.spacing(3),
              width: '100%',
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: theme.spacing(2),
              borderRadius: theme.spacing(0.5),
            }}
          >
            <Stack spacing={0.25}>
              <ThreadsTitle
                variant={titleVariant}
                fw={displayLarge.fontWeight || 900}
                sx={{
                  width: isMobile ? '100%' : 'calc(100% - 16px)',
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  wordBreak: 'keep-all',
                  overflowWrap: 'break-word',
                  fontSize: displayLarge.fontSize,
                }}
              >
                {collection.title}
              </ThreadsTitle>
            </Stack>
            <WriterInfo
              size={isMobile ? 'small' : 'default'}
              profileImage={collection.profileImage}
              userID={collection.userID}
              username={collection.username}
              name={collection.name}
              showUserPageLink={!isMe}
            />
          </Box>
        )}
      </CardContainerRetro>
    </NavLink>
  );
}
